import React, { useState, useContext, useEffect, useMemo } from "react";
import clsx from "clsx";

import {
  makeStyles,
  Drawer,
  AppBar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Chip,
} from "@material-ui/core";

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
  Moon as Brightness4Icon,
  RefreshCw as CachedIcon,
  Sun as Brightness7Icon
} from "lucide-react";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import ChatPopover from "../pages/Chat/ChatPopover";

import { useDate } from "../hooks/useDate";

import ColorModeContext from "./themeContext";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import VersionControl from "../components/VersionControl";

// logos (colored for white sidebar)
import logo from "../assets/logo.svg";

const backendUrl = getBackendUrl();
const drawerExpandedWidth = 260;
const drawerCollapsedWidth = 64;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      height: "100dvh",
    },
    "& .MuiButton-outlinedPrimary": {
      color: "#88d03e",
      border: "1.5px solid #88d03e",
      borderRadius: "10px",
      "&:hover": {
        backgroundColor: "rgba(136, 208, 62, 0.12)",
        borderColor: "#63ae18",
      },
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: "#88d03e",
    },
  },

  chip: { 
    background: "linear-gradient(135deg, #88d03e 0%, #63ae18 100%)", 
    color: "#1b1f22",
    fontWeight: 600,
    fontSize: "12px",
    borderRadius: "20px",
    padding: "0 4px",
  },
  
  avatar: { width: "100%" },

  toolbar: {
    paddingRight: 24,
    paddingLeft: 16,
    background: "linear-gradient(135deg, #88d03e 0%, #63ae18 100%)",
    gap: theme.spacing(1),
    overflow: "visible",
    minHeight: 60,
    boxShadow: "0 4px 20px rgba(136, 208, 62, 0.25)",
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(1.5),
      paddingLeft: theme.spacing(1.5),
      minHeight: 56,
      gap: theme.spacing(0.5),
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
    },
  },

  topbarScroller: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flex: "1 1 0%",
    minWidth: 0,
    maxWidth: "100%",
    flexWrap: "nowrap",
    justifyContent: "flex-end",
    overflowX: "visible",
    "& > *": { flex: "0 0 auto" },
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      touchAction: "pan-x",
      overscrollBehaviorX: "contain",
      msOverflowStyle: "none",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 12px",
    minHeight: "64px",
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: "relative",
  },
  
  toolbarIconCollapsed: {
    justifyContent: "center",
    padding: "16px 8px",
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    boxShadow: "none",
  },
  
  appBarShift: {
    marginLeft: drawerExpandedWidth,
    width: `calc(100% - ${drawerExpandedWidth}px)`,
    transition: "width 200ms ease, margin 200ms ease",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
    },
  },

  menuButtonHidden: { display: "none" },

  title: {
    flexGrow: 0,
    fontSize: 14,
    color: "white",
    marginLeft: theme.spacing(1),
    fontWeight: 500,
    opacity: 0.95,
    [theme.breakpoints.down("sm")]: { display: "none" },
  },

  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerExpandedWidth,
    transition: "width 200ms ease, margin 200ms ease",
    overflowX: "hidden",
    overflowY: "hidden",
    background: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: "4px 0 20px rgba(0, 0, 0, 0.03)",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
  },
  
  drawerPaperClose: {
    overflowX: "hidden",
    overflowY: "hidden",
    transition: "width 200ms ease, margin 200ms ease",
    width: drawerCollapsedWidth,
  },

  appBarSpacer: { 
    minHeight: 60,
    flexShrink: 0,
    [theme.breakpoints.up("sm")]: {
      minHeight: 0,
    },
  },

  content: { 
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    height: "100%",
    overflow: "hidden",
    position: "relative",
    backgroundColor: theme.palette.background.default,
  },

  contentBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },

  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },

  containerWithScroll: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "8px 0",
    scrollbarWidth: "thin",
    scrollbarColor: `${theme.palette.primary.main} transparent`,
    "&::-webkit-scrollbar": { 
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.primary.main,
      borderRadius: "10px",
      border: "2px solid transparent",
      backgroundClip: "padding-box",
      transition: `background-color 150ms ease-out`,
      "&:hover": {
        background: theme.palette.secondary.main,
        backgroundClip: "padding-box",
      },
    },
  },

  logoImg: {
    height: 42,
    maxWidth: 160,
    objectFit: "contain",
    transition: "opacity 150ms ease",
  },
  
  logoImgCollapsed: {
    height: 36,
    width: 36,
    objectFit: "contain",
    transition: "opacity 150ms ease",
  },

  // Sidebar bottom toolbar styles
  sidebarBottomToolbar: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minHeight: "auto",
  },

  sidebarBottomToolbarCollapsed: {
    padding: theme.spacing(1),
    alignItems: "center",
  },

  userSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
    borderRadius: "12px",
    background: "linear-gradient(135deg, #88d03e 0%, #63ae18 100%)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(136, 208, 62, 0.3)",
    },
  },

  userSectionCollapsed: {
    justifyContent: "center",
    padding: theme.spacing(0.75),
  },

  avatar2: {
    width: 48,
    height: 48,
    cursor: "pointer",
    borderRadius: "50%",
    border: "3px solid rgba(255, 255, 255, 0.9)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    transition: "all 0.2s ease",
    "&:hover": {
      border: "3px solid white",
      transform: "scale(1.05)",
    },
  },

  avatarCollapsed: {
    width: 40,
    height: 40,
  },

  userInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25),
    opacity: 1,
    transition: "opacity 150ms ease",
  },

  userName: {
    color: "white",
    fontWeight: 600,
    fontSize: "14px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  userCompany: {
    color: "rgba(255, 255, 255, 0.85)",
    fontSize: "12px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  actionsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
  },

  actionsRowCollapsed: {
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },

  sidebarIconButton: {
    color: theme.palette.text.primary,
    padding: 8,
    borderRadius: "10px",
    transition: "all 0.2s ease",
    minWidth: 36,
    minHeight: 36,
    "&:hover": {
      backgroundColor: "rgba(136, 208, 62, 0.12)",
      color: theme.palette.primary.main,
    },
    "& svg": {
      fontSize: "24px",
      width: "24px",
      height: "24px",
    },
  },

  // Override white icon colors from toolbar components
  sidebarActionIcon: {
    "& button": {
      color: `${theme.palette.text.primary} !important`,
      padding: 8,
      borderRadius: "10px",
      transition: "all 0.2s ease",
      minWidth: 36,
      minHeight: 36,
      "&:hover": {
        backgroundColor: "rgba(136, 208, 62, 0.12)",
        color: `${theme.palette.primary.main} !important`,
      },
    },
    "& svg": {
      color: `${theme.palette.text.primary} !important`,
      fontSize: "24px !important",
      width: "24px !important",
      height: "24px !important",
    },
  },

  iconButton: {
    color: "white",
    padding: 8,
    borderRadius: "10px",
    transition: "all 0.2s ease",
    minWidth: 36,
    minHeight: 36,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.15)",
    },
    "& svg": {
      fontSize: "24px",
      width: "24px",
      height: "24px",
    },
  },

  menuButton: {
    color: "white",
    padding: 8,
    borderRadius: "10px",
    marginRight: 8,
    transition: "all 0.2s ease",
    minWidth: 36,
    minHeight: 36,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.15)",
    },
    "& svg": {
      fontSize: "24px",
      width: "24px",
      height: "24px",
    },
  },

  chevronButton: {
    color: theme.palette.text.primary,
    padding: 6,
    borderRadius: "8px",
    transition: "all 0.2s ease",
    position: "absolute",
    right: 8,
    "&:hover": {
      backgroundColor: "rgba(136, 208, 62, 0.12)",
      color: theme.palette.primary.main,
    },
    "& svg": {
      fontSize: "24px",
    },
  },
  
  chevronButtonCollapsed: {
    position: "static",
    margin: "0 auto",
    color: "#63ae18",
    "&:hover": {
      backgroundColor: "rgba(99,174,24,0.12)",
      color: "#63ae18",
    },
  },

  userMenu: {
    "& .MuiPaper-root": {
      borderRadius: "12px",
      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
      border: `1px solid ${theme.palette.divider}`,
      minWidth: 180,
      marginTop: 8,
    },
    "& .MuiMenuItem-root": {
      padding: "12px 20px",
      fontSize: "14px",
      fontWeight: 500,
      color: theme.palette.text.primary,
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: "rgba(136, 208, 62, 0.12)",
        color: theme.palette.primary.main,
      },
    },
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#88d03e",
    color: "#1b1f22",
    boxShadow: `0 0 0 2px #1b1f22`,
    width: 12,
    height: 12,
    borderRadius: "50%",
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.4s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": { transform: "scale(.8)", opacity: 1 },
    "100%": { transform: "scale(2.2)", opacity: 0 },
  },
}))(Badge);

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userToken, setUserToken] = useState("disabled");
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user, socket } = useContext(AuthContext);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);

  const mainListItems = useMemo(
    () => <MainListItems drawerOpen={drawerOpen} collapsed={!drawerOpen} />,
    [user, drawerOpen]
  );

  const settings = useSettings();

  useEffect(() => {
    const getSetting = async () => {
      try {
        // Only make the API call if user is authenticated and has valid data
        if (user && user.id) {
          const response = await settings.get("wtV");
          setUserToken("disabled");
        }
      } catch (err) {
        // Silently fail if user is not authenticated or token is invalid
        console.debug("Settings fetch failed:", err.message);
      }
    };
    getSetting();
  }, [user?.id, settings]);

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    } else {
      setDrawerOpen(false);
    }
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [document.body.offsetWidth]);

  useEffect(() => {
    if (document.body.offsetWidth < 600) setDrawerVariant("temporary");
    else setDrawerVariant("permanent");
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = user?.companyId;
    const userId = user?.id;
    
    // Only set up socket listeners if user has valid data and socket is available
    if (companyId && userId && socket && Object.keys(socket).length > 0) {
      const ImageUrl = user.profileImage;
      if (ImageUrl !== undefined && ImageUrl !== null)
        setProfileUrl(`${backendUrl}/public/avatar/${ImageUrl}`);
      else setProfileUrl(`${process.env.FRONTEND_URL}/nopicture.png`);

      const onCompanyAuthLayout = (data) => {
        if (data.user.id === +userId) {
          toastError("Sua conta foi acessada em outro computador.");
          setTimeout(() => {
            localStorage.clear();
            window.location.reload();
          }, 1000);
        }
      };

      socket.on(`company-${companyId}-auth`, onCompanyAuthLayout);

      socket.emit("userStatus");
      const interval = setInterval(() => {
        socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => {
        socket.off(`company-${companyId}-auth`, onCompanyAuthLayout);
        clearInterval(interval);
      };
    }
  }, [socket, user?.id, user?.companyId]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };
  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };
  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };
  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };
  const handleRefreshPage = () => window.location.reload(false);

  if (loading) return <BackdropLoading />;

  // src da logo com fallback ao tema
  const logoSrc = logo;

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(classes.drawerPaper, !drawerOpen && classes.drawerPaperClose),
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <div className={clsx(
          classes.toolbarIcon,
          !drawerOpen && classes.toolbarIconCollapsed
        )}>
          {drawerOpen ? (
            <img
              src={logoSrc}
              alt="logo"
              className={classes.logoImg}
            />
          ) : null}
          <IconButton 
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.chevronButton,
              !drawerOpen && classes.chevronButtonCollapsed
            )}
          >
            {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </div>
        <List className={classes.containerWithScroll}>
          <MainListItems collapsed={!drawerOpen} />
        </List>
        <Divider style={{ background: theme.palette.divider }} />
        
        {/* Bottom Toolbar - User & Actions */}
        <div className={clsx(
          classes.sidebarBottomToolbar,
          !drawerOpen && classes.sidebarBottomToolbarCollapsed
        )}>
          {/* User Section - Emphasized */}
          <div 
            className={clsx(
              classes.userSection,
              !drawerOpen && classes.userSectionCollapsed
            )}
            onClick={handleMenu}
          >
            <StyledBadge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
            >
              <Avatar 
                alt={user?.name} 
                className={clsx(
                  classes.avatar2,
                  !drawerOpen && classes.avatarCollapsed
                )} 
                src={profileUrl} 
              />
            </StyledBadge>
            {drawerOpen && (
              <div className={classes.userInfo}>
                <Typography className={classes.userName}>
                  {user?.name}
                </Typography>
                <Typography className={classes.userCompany}>
                  {user?.company?.name}
                </Typography>
              </div>
            )}
          </div>

          {/* Actions Row */}
          <div className={clsx(
            classes.actionsRow,
            !drawerOpen && classes.actionsRowCollapsed
          )}>
            <span className={classes.sidebarActionIcon}>
              <VersionControl />
            </span>
            
            <IconButton
              onClick={handleRefreshPage}
              aria-label={i18n.t("mainDrawer.appBar.refresh")}
              className={classes.sidebarIconButton}
              title={i18n.t("mainDrawer.appBar.refresh")}
            >
              <CachedIcon size={20} />
            </IconButton>

            <span className={classes.sidebarActionIcon}>
              <NotificationsVolume setVolume={setVolume} volume={volume} />
            </span>

            <span className={classes.sidebarActionIcon}>
              {user.id && <NotificationsPopOver volume={volume} />}
            </span>

            <span className={classes.sidebarActionIcon}>
              <AnnouncementsPopover />
            </span>
            
            <span className={classes.sidebarActionIcon}>
              <ChatPopover />
            </span>
          </div>
        </div>

        {/* User Menu */}
        <UserModal
          open={userModalOpen}
          onClose={() => setUserModalOpen(false)}
          onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
          userId={user?.id}
        />
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={menuOpen}
          onClose={handleCloseMenu}
          className={classes.userMenu}
        >
          <MenuItem onClick={handleOpenUserModal}>
            {i18n.t("mainDrawer.appBar.user.profile")}
          </MenuItem>
          <MenuItem onClick={handleClickLogout}>
            {i18n.t("mainDrawer.appBar.user.logout")}
          </MenuItem>
        </Menu>
      </Drawer>

      {/* Minimal AppBar - Only for mobile menu */}
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        color="primary"
        style={{ 
          display: greaterThenSm ? 'none' : 'block' 
        }}
      >
        <div className={classes.toolbar}>
          {/* Menu button - mobile only */}
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>

          {/* Title (optional - can be removed) */}
          <Typography component="h2" variant="h6" color="inherit" noWrap className={classes.title}>
            {user?.company?.name || "Dashboard"}
          </Typography>
        </div>
      </AppBar>

      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <div className={classes.contentBody}>
          {children ? children : null}
        </div>
      </main>
    </div>
  );
};

export default LoggedInLayout;
